import {
    addDays, differenceInDays,
    differenceInMonths,
    differenceInYears, endOfDay, endOfMonth, isSameDay,
    isSameMonth,
    isSameYear, isThisYear, startOfDay, startOfMonth, subDays, subMonths,
    subYears
} from 'date-fns';
import { formatLocale, getCost } from 'lib';

function getChartData(data, dateRange) {
  let chartData = [];
  const now = new Date();

  if (!dateRange) {
    const sorted = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    dateRange = [new Date(sorted[0].createdAt), new Date(sorted[data.length - 1].createdAt)];
  }

  const daysDiff = differenceInDays(dateRange[0], dateRange[1]) + 1,
    monthsDiff = differenceInMonths(startOfMonth(dateRange[0]), startOfMonth(dateRange[1])) + 1,
    yearsDiff = differenceInYears(startOfMonth(dateRange[0]), endOfMonth(dateRange[1]));

  switch (true) {
    case yearsDiff >= 1:
      for (let i = 0; i <= yearsDiff; i++) {
        const current = subYears(dateRange[0], i),
          label = formatLocale(i === 0 ? dateRange[0] : dateRange[1], 'MMM yyy');

        const filtered = data
          .filter((x) => isSameYear(new Date(x.createdAt), current))
          .reduce((x, y) => {
            const cost = getCost(y.rates, y.currency, y.amount);
            return x + cost.convertedPrice - (y.fee / 100) * cost.convertedPrice;
          }, 0);

        chartData.push({
          date: current,
          amount: filtered / 100,
          label
        });
      }
      break;

    case daysDiff <= 7:
      for (let i = 0; i < data.length; i++) {
        const current = data[i],
          createdAt = new Date(current.createdAt),
          label = formatLocale(
            createdAt,
            differenceInDays(dateRange[1], now) <= 7 ? 'E' : 'dd MMM'
          ),
          cost = getCost(current.rates, current.currency, current.amount),
          amount = cost.convertedPrice - (current.fee / 100) * cost.convertedPrice;

        const found = chartData.findIndex((x) => x.label === label);

        if (found > -1) {
          chartData[found].amount = (chartData[found]?.amount || 0) + amount / 100;

          continue;
        }

        chartData.push({
          amount: amount / 100,
          date: createdAt,
          label
        });
      }

      for (let i = 0; i <= daysDiff; i++) {
        const date = subDays(new Date(dateRange[0]), i);
        const isCurrentWeek = differenceInDays(dateRange[1], date) <= 7;
        const label = formatLocale(date, isCurrentWeek ? 'E' : 'dd MMM');

        const found = chartData.filter((x) => {
          return x.label === label;
        });
        if (found.length) continue;

        chartData.push({
          amount: 0,
          date,
          label
        });
      }
      break;

    case daysDiff <= 62:
      let weekGroups = [];
      const weeks = Math.ceil(daysDiff / 7);

      /*
        Turn days to weeks to fit the chart
      */
      for (let i = 0; i < weeks; i++) {
        for (let t = 1; t <= 7; t++) {
          const value = addDays(addDays(dateRange[1], i * 7), t);
          // Above code remains unselected dates behind,
          // below breaks the loop at that point
          if (startOfDay(value) > endOfDay(dateRange[0])) break;

          if (weekGroups[i]) {
            weekGroups[i].push(value);

            continue;
          }

          weekGroups[i] = [value];
        }
      }

      /*
        Generate chart data for each group
      */
      for (let i = 0; i < weekGroups.length; i++) {
        const current = weekGroups[i];

        const reduced = current.reduce((x, y) => {
          const found = data
            .filter((z) => isSameDay(new Date(z.createdAt), y))
            .reduce((x, y) => {
              const cost = getCost(y.rates, y.currency, y.amount);
              return x + cost.convertedPrice - (y.fee / 100) * cost.convertedPrice;
            }, 0);

          return x + (found / 100 || 0);
        }, 0);

        chartData.push({
          label: `${formatLocale(current[0], 'dd MMM')} - ${formatLocale(current[current.length - 1], 'dd MMM')}`,
          date: current[0],
          amount: reduced
        });
      }
      break;

    case monthsDiff > 1:
      for (let i = 0; i < monthsDiff; i++) {
        const currentMonth = subMonths(dateRange[0], i),
          label = formatLocale(currentMonth, `MMM${isThisYear(currentMonth) ? '' : ' yyy'}`),
          subLabel =
            i === 0
              ? formatLocale(currentMonth, 'd ')
              : i + 1 === monthsDiff
                ? formatLocale(dateRange[1], 'd ')
                : '';

        const filtered = data
          .filter((x) => isSameMonth(new Date(x.createdAt), currentMonth))
          .reduce((x, y) => {
            const cost = getCost(y.rates, y.currency, y.amount);
            return x + cost.convertedPrice - (y.fee / 100) * cost.convertedPrice;
          }, 0);

        chartData.push({
          label: `${subLabel}${label}`,
          date: currentMonth,
          amount: filtered / 100
        });
      }
      break;

    default:
  }

  return chartData.sort((a, b) => a.date - b.date);
}

export default getChartData;
